import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/format";
import { CalendarClock, ArrowLeftRight, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function NearExpiry() {
  const { can } = useAuth();
  const { branches } = useBranch();
  const [tab, setTab] = useState("near");
  const [days, setDays] = useState("30");
  const [fBranch, setFBranch] = useState("all");
  const [rows, setRows] = useState([]);
  const [sug, setSug] = useState(null);

  const load = useCallback(() => {
    const params = { branch_id: fBranch === "all" ? undefined : fBranch };
    if (tab === "quarantined") params.quarantined = true; else params.days = Number(days);
    api.get("/near-expiry", { params }).then((r) => setRows(r.data)).catch(() => {});
  }, [tab, days, fBranch]);
  useEffect(() => { load(); }, [load]);

  const act = async (b, path, msg) => {
    try { await api.post(`/batches/${b.id}/${path}`); toast.success(msg); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const writeOff = (b) => { if (window.confirm(`Write off ${b.quantity_on_hand} of ${b.generic_name} (batch ${b.batch_no}) to zero?`)) act(b, "write-off", "Batch written off"); };
  const suggest = async (b) => {
    try { const { data } = await api.get(`/near-expiry/${b.id}/transfer-suggestion`);
      if (!data.suggested) return toast.info("No branch shows recent consumption for this product");
      setSug({ ...data.suggested, batch: b });
    } catch (e) { toast.error(apiError(e)); }
  };
  const createTransfer = async () => {
    try {
      await api.post("/transfers", { from_branch_id: sug.from_branch_id, to_branch_id: sug.to_branch_id,
        lines: [{ product_id: sug.product_id, quantity: sug.suggested_qty }], note: `Near-expiry redistribution (${sug.batch.batch_no})` });
      toast.success("Transfer created — dispatch it from Transfers"); setSug(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Near-Expiry & Quarantine" subtitle="Pull stock ≤30 days to expiry off the shelf, redistribute or write off"
        actions={<div className="flex gap-2">
          {tab === "near" && <Select value={days} onValueChange={setDays}><SelectTrigger data-testid="ne-days" className="w-32"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="30">≤ 30 days</SelectItem><SelectItem value="60">≤ 60 days</SelectItem><SelectItem value="90">≤ 90 days</SelectItem></SelectContent></Select>}
          <Select value={fBranch} onValueChange={setFBranch}><SelectTrigger data-testid="ne-branch" className="w-44"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select>
        </div>} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="near" data-testid="ne-tab-near">Near expiry</TabsTrigger>
          <TabsTrigger value="quarantined" data-testid="ne-tab-quarantined">Quarantined</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Card className="shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                <tr><th className="py-2.5 px-3">Medicine</th><th className="py-2.5 px-3">Batch</th><th className="py-2.5 px-3">Branch</th><th className="py-2.5 px-3">Expiry</th><th className="py-2.5 px-3 text-right">Days</th><th className="py-2.5 px-3 text-right">Qty</th><th className="py-2.5 px-3"></th></tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-muted-foreground"><CalendarClock className="h-8 w-8 mx-auto mb-2 opacity-40" />Nothing here</td></tr>}
                {rows.map((b) => (
                  <tr key={b.id} className="border-t border-border hover:bg-muted/50">
                    <td className="py-2 px-3 font-medium text-foreground">{b.generic_name}<span className="text-xs text-muted-foreground ml-1">{b.sku}</span></td>
                    <td className="py-2 px-3 font-mono text-xs">{b.batch_no}</td>
                    <td className="py-2 px-3 text-muted-foreground">{b.branch_name}</td>
                    <td className="py-2 px-3 text-xs">{fmtDate(b.expiry_date)}</td>
                    <td className="py-2 px-3 text-right"><StatusBadge value={b.days_to_expiry != null && b.days_to_expiry <= 0 ? "out" : "pending"} label={b.days_to_expiry != null ? `${b.days_to_expiry}d` : "—"} /></td>
                    <td className="py-2 px-3 text-right font-mono">{b.quantity_on_hand}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      {can("inventory.adjust") && tab === "near" && <>
                        <Button size="sm" variant="ghost" data-testid={`suggest-${b.id}`} onClick={() => suggest(b)} title="Suggest transfer"><ArrowLeftRight className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="outline" className="ml-1" data-testid={`quarantine-${b.id}`} onClick={() => act(b, "quarantine", "Batch quarantined")}><ShieldAlert className="h-3.5 w-3.5 mr-1" />Quarantine</Button>
                      </>}
                      {can("inventory.adjust") && tab === "quarantined" && <Button size="sm" variant="outline" data-testid={`unquarantine-${b.id}`} onClick={() => act(b, "unquarantine", "Returned to shelf")}>Return to shelf</Button>}
                      {can("inventory.adjust") && <Button size="sm" variant="ghost" className="ml-1 text-rose-600" data-testid={`writeoff-${b.id}`} onClick={() => writeOff(b)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!sug} onOpenChange={(o) => !o && setSug(null)}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>Suggested transfer</DialogTitle><DialogDescription>Move close-to-expiry stock to a branch that's selling it.</DialogDescription></DialogHeader>
          {sug && <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Medicine</span><span className="font-medium">{sug.batch.generic_name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Move to</span><span className="font-medium">{sug.to_branch_name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Sold there (60d)</span><span className="font-mono">{sug.consumption_60d}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Current stock there</span><span className="font-mono">{sug.current_stock}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Suggested quantity</span><span className="font-mono font-semibold">{sug.suggested_qty}</span></div>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setSug(null)}>Cancel</Button><Button data-testid="create-transfer-btn" onClick={createTransfer}>Create transfer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
